import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI } from '../lib/api';
import { CheckCircle, Loader2, Shield } from 'lucide-react';
import kivoIcon from '../assets/logo/kivo-icon-only.png';
import { toast } from 'sonner';

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const FEATURES = [
  'School email monitoring + task extraction',
  'Automated caregiver WhatsApp check-ins',
  'Morning briefing + evening summary',
  'Pickup tracking + urgent alerts',
  'Parent dashboard with full history',
];

export const Subscribe = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await paymentAPI.getStatus();
        if (data.subscription_status === 'active' || data.subscription_status === 'trial') {
          navigate('/parent', { replace: true });
          return;
        }
      } catch (err) {
        // Continue to show subscribe page
      }
      setChecking(false);
    };
    checkStatus();
  }, [navigate]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async () => {
    setLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      const { data } = await paymentAPI.subscribe();
      if (!data.ok) {
        toast.error(data.error || 'Failed to create subscription');
        setLoading(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        subscription_id: data.subscription_id,
        name: 'Kivo',
        description: 'Monthly Family Plan — Rs. 1,499/month',
        image: '',
        prefill: {
          email: user?.email || '',
        },
        theme: {
          color: '#7c5cfc',
        },
        handler: async (response) => {
          try {
            await paymentAPI.verify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Subscription activated! Welcome to Kivo.');
            navigate('/parent', { replace: true });
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <img src={kivoIcon} alt="Kivo" className="h-10 w-auto" />
            <span className="text-xl font-bold text-slate-800 font-heading">Kivo</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
            Start your subscription
          </h1>
          <p className="text-sm text-slate-500">
            You're almost there! Subscribe to unlock all Kivo features.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-baseline justify-center gap-1 mb-1">
            <span className="text-3xl font-bold text-slate-900 font-heading">Rs. 1,499</span>
            <span className="text-sm text-slate-500">/month</span>
          </div>
          <p className="text-center text-xs text-slate-400 mb-6">per family, cancel anytime</p>

          <ul className="space-y-2.5 mb-6">
            {FEATURES.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-800 transition-colors disabled:opacity-50"
            data-testid="subscribe-cta"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Subscribe now'
            )}
          </button>

          <div className="flex items-center justify-center gap-1.5 mt-4">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400">Secured by Razorpay</span>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          By subscribing, you agree to our{' '}
          <a href="/terms" className="text-slate-500 hover:underline">Terms</a>
          {' '}and{' '}
          <a href="/privacy" className="text-slate-500 hover:underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default Subscribe;
